import React, { useMemo, useState, memo, useCallback } from 'react';
import { EuiDataGrid, EuiFlexGroup, EuiFlexItem, EuiPagination, EuiText, EuiLink } from '@elastic/eui';
import ContextMenu from '../../../components/Global/Pagination/ContextMenu';
import { useHistory } from 'react-router-dom';
import SubscriptionActions from './SubscriptionActions';

const OutletsDataGrid = ({
    getAllOutlets,
    activePage,
    rowSize,
    onPageChange,
    onRowSizeChange,
    onDeleteOutlet,
    totalPages,
    onRefresh,
}) => {
    const history = useHistory();
    const outlets = useMemo(() => getAllOutlets?.subscription_requests?.data || [], [getAllOutlets]);
    const mappedOutlets = useMemo(() => {
        return outlets.map((item) => {
            return {
                outlet_name: <EuiText>{item.legal_name || '--'}</EuiText>,
                outlet_status: <EuiText>{item.outlet_status || '--'}</EuiText>,
                payment_status: <EuiText>{item.status || '--'}</EuiText>,
                start_date: <EuiText>{item.started_at || '--'}</EuiText>,
                renewal_date: <EuiText>{item.renewal_at || '--'}</EuiText>,
                payment_link: item.subscription_id,
                id: item.id, // Pass ID for actions
            };
        });
    }, [outlets]);
    // Columns definition
    const columns = useMemo(() => {
        return [
            {
                id: 'outlet_name',
                display: 'Outlet Name',
                displayAsText: 'Outlet Name',
                isSortable: true,
                // renderCellValue handled by mappedOutlets
            },
            {
                id: 'outlet_status',
                display: 'Outlet Status',
                displayAsText: 'Outlet Status',
                isSortable: true,
                // renderCellValue handled by mappedOutlets
            },
            {
                id: 'payment_status',
                display: 'Payment Status',
                displayAsText: 'Payment Status',
                isSortable: true,
                // renderCellValue handled by mappedOutlets
            },
            {
                id: 'start_date',
                display: 'Start Date',
                displayAsText: 'Start Date',
                isSortable: true,
                // renderCellValue handled by mappedOutlets
            },
            {
                id: 'renewal_date',
                display: 'Renewal Date',
                displayAsText: 'Renewal Date',
                isSortable: true,
                // renderCellValue handled by mappedOutlets
            },
            {
                id: 'payment_link',
                display: 'Payment Link',
                displayAsText: 'Payment Link',
                isSortable: false,
                renderCellValue: ({ rowIndex }) => {
                    const outlet = mappedOutlets[rowIndex];

                    if (!outlet?.payment_link) {
                        return <EuiText>--</EuiText>;
                    }
                    return (
                        <EuiText>
                            <EuiLink
                                onClick={() => {
                                    // Check if payment is already done
                                    // const paymentStatus = outlet.payment_status?.props?.children?.toLowerCase();
                                    // const isPaymentComplete = [
                                    //     'txn_success',
                                    //     'active',
                                    //     'paid',
                                    //     'done',
                                    //     'success',
                                    // ].includes(paymentStatus);

                                    history.push({
                                        pathname: `/subscription-payment/${outlet.payment_link}`,
                                        // state: {
                                        //     outletId: outlet.id,
                                        //     outletName: originalOutlet?.legal_name,
                                        //     planName: 'Monthly Store Subscription (La Milano Deal)',
                                        //     unitPrice: '2,950.00',
                                        //     paymentStatus: originalOutlet?.status,
                                        //     isPaymentComplete,
                                        //     ...originalOutlet,
                                        // },
                                    });
                                }}
                            >
                                {outlet.payment_link}
                            </EuiLink>
                        </EuiText>
                    );
                },
            },
            {
                id: 'actions',
                display: 'Actions',
                displayAsText: 'Actions',
                isSortable: false,
                width: 100,
                align: 'end',
                renderCellValue: ({ rowIndex }) => {
                    const outlet = outlets[rowIndex];
                    return <SubscriptionActions subscription={outlet} onRefresh={onRefresh} />;
                },
            },
        ];
    }, [mappedOutlets, outlets, history, onRefresh]);

    const [visibleColumns, setVisibleColumns] = useState(() => columns.map(({ id }) => id));

    const renderCellValue = useMemo(() => {
        return ({ rowIndex, columnId, setCellProps }) => {
            const column = columns.find((col) => col.id === columnId);
            if (column && column.renderCellValue) {
                return column.renderCellValue({ rowIndex, columnId, setCellProps });
            }
            return mappedOutlets.hasOwnProperty(rowIndex) ? mappedOutlets[rowIndex][columnId] || '--' : null;
        };
    }, [mappedOutlets, columns]);

    const PAGE_COUNT = totalPages;

    const onChangeRowSizeInternal = useCallback(
        (row_size) => {
            onPageChange(0);
            onRowSizeChange(row_size);
        },
        [onPageChange, onRowSizeChange]
    );

    const goToPage = (pageNumber) => onPageChange(pageNumber);

    const popOverItems = useMemo(() => {
        return [
            {
                key: '10 rows',
                onClick: (closePopover) => {
                    closePopover();
                    onChangeRowSizeInternal(10);
                },
                iconSize: 10,
            },
            {
                key: '20 rows',
                onClick: (closePopover) => {
                    closePopover();
                    onChangeRowSizeInternal(20);
                },
                iconSize: 20,
            },
            {
                key: '50 rows',
                onClick: (closePopover) => {
                    closePopover();
                    onChangeRowSizeInternal(50);
                },
                iconSize: 50,
            },
            {
                key: '100 rows',
                onClick: (closePopover) => {
                    closePopover();
                    onChangeRowSizeInternal(100);
                },
                iconSize: 100,
            },
        ];
    }, [onChangeRowSizeInternal]);

    if (outlets.length === 0) {
        return (
            <EuiFlexGroup justifyContent='center' alignItems='center' style={{ height: '200px' }}>
                <EuiFlexItem grow={false}>
                    <EuiText color='subdued'>No outlets were created</EuiText>
                </EuiFlexItem>
            </EuiFlexGroup>
        );
    }

    return (
        <>
            <EuiDataGrid
                aria-label='Outlets List Datagrid'
                columns={columns}
                columnVisibility={{ visibleColumns, setVisibleColumns }}
                rowCount={mappedOutlets.length} // Use mappedOutlets.length
                renderCellValue={renderCellValue}
                // TODO: Add sorting and pagination handlers when integrating with API
                // inMemory={{ level: 'sorting' }}
                // sorting={{ columns: sortingColumns, onSort }}
            />
            <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
                <EuiFlexItem grow={false}>
                    <ContextMenu rowSize={rowSize} popOverItems={popOverItems} />
                    {/* Need to import ContextMenu or implement similar logic */}
                </EuiFlexItem>

                <EuiFlexItem grow={false}>
                    <EuiPagination
                        aria-label='Custom pagination example'
                        pageCount={PAGE_COUNT ? PAGE_COUNT : ''}
                        activePage={activePage}
                        onPageClick={goToPage}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );
};

export default memo(OutletsDataGrid);
